import { Button } from "~/atoms/button";
import {
  Popover,
  PopoverClose,
  PopoverContent,
  PopoverTrigger,
} from "~/atoms/popover";
import { Filter as FilterIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { ALL_OPTION_VALUE, type FilterWithOption } from "~/hooks/useFilter";
import { Label } from "~/atoms/label";
import { ControlledCombobox } from "./ControlledCombobox";

interface FilterPopoverProps<T> {
  filters: FilterWithOption<T>[];
  activeFiltersCount: number;
  onClearFilters: () => void;
  onSelectFilter: (filterId: string, optionIds: string[]) => void;
}

export function FilterPopover<T>({
  filters,
  activeFiltersCount,
  onClearFilters,
  onSelectFilter,
}: FilterPopoverProps<T>) {
  const { t } = useTranslation();

  let buttonText = `${t(($) => $.commons.filter.title)}`;
  if (activeFiltersCount > 0) buttonText += ` (${activeFiltersCount})`;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline">
          <FilterIcon />
          {buttonText}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-96 space-y-2">
        <FilterRows filters={filters} onSelectFilter={onSelectFilter} />
        <div className="col-span-3 grid grid-cols-2 gap-2">
          <Button variant="outline" size="sm" onClick={onClearFilters}>
            {t(($) => $.commons.filter.clear)}
          </Button>
          <PopoverClose asChild>
            <Button size="sm">{t(($) => $.commons.buttons.done)}</Button>
          </PopoverClose>
        </div>
      </PopoverContent>
    </Popover>
  );
}

interface FilterRowsProps<T> {
  filters: FilterWithOption<T>[];
  onSelectFilter: (filterId: string, optionIds: string[]) => void;
}

const FilterRows = <T,>({ filters, onSelectFilter }: FilterRowsProps<T>) => {
  const { t } = useTranslation();

  return filters.map((filter) => (
    <div key={filter.id}>
      <Label>{filter.label}</Label>
      {filter.isMulti ? (
        <ControlledCombobox
          isMulti
          options={filter.options}
          value={filter.optionIds}
          placeholder={
            filter.optionIds.length === 0
              ? t(($) => $.commons.select)
              : t(($) => $.commons.filter.selectedCount, {
                  count: filter.optionIds.length,
                })
          }
          onChange={(optionIds) => onSelectFilter(filter.id, optionIds)}
        />
      ) : (
        <ControlledCombobox
          options={filter.options}
          value={filter.optionIds[0] ?? ALL_OPTION_VALUE}
          onChange={(optionId) => onSelectFilter(filter.id, [optionId])}
        />
      )}
    </div>
  ));
};
